import type Swiper from 'swiper';

// Dynamic card sizing constants
export const BASE_CARD_WIDTH_DESKTOP = 230; // Base width in pixels (md breakpoint)
export const BASE_CARD_WIDTH_MOBILE = 148; // Base width in pixels (9.25rem = 148px)
export const SPACE_BETWEEN = 16; // Space between cards
export const MOBILE_GRID_ROWS = 2; // Mobile has 2 rows

/**
 * Resolves the number of rows from Swiper configuration
 */
export function resolveSwiperRows(swiper: Swiper): number {
	const paramsRows = swiper.params.grid?.rows;
	if (typeof paramsRows === 'number' && paramsRows > 0) {
		return paramsRows;
	}

	const originalRows = swiper.originalParams?.grid?.rows;
	if (typeof originalRows === 'number' && originalRows > 0) {
		return originalRows;
	}

	return 1;
}

/**
 * Calculates how many slides are visible per view
 */
export function calculateSlidesPerView(swiper: Swiper): number {
	const slidesPerViewParam = swiper.params.slidesPerView;
	if (typeof slidesPerViewParam === 'number') {
		return Math.max(1, slidesPerViewParam);
	}

	const slide = swiper.slides?.[0] as HTMLElement | undefined;
	const slideWidth = slide?.offsetWidth ?? swiper.width;
	const spaceBetween =
		typeof swiper.params.spaceBetween === 'number'
			? swiper.params.spaceBetween
			: 0;

	if (slideWidth <= 0) {
		return 1;
	}

	const totalWidth = slideWidth + spaceBetween;
	if (totalWidth <= 0) {
		return 1;
	}

	return Math.max(1, Math.floor((swiper.width + spaceBetween) / totalWidth));
}

/**
 * Gets swiper metrics including rows, columns, and visibility calculations
 */
export function getSwiperMetrics(swiper: Swiper, totalSlides: number) {
	const rows = resolveSwiperRows(swiper);
	const slidesPerView = calculateSlidesPerView(swiper);

	const totalColumns = rows > 1 ? Math.ceil(totalSlides / rows) : totalSlides;

	const visibleColumns = Math.min(slidesPerView, totalColumns);

	let currentColumn =
		rows > 1 ? Math.floor(swiper.activeIndex / rows) : swiper.activeIndex;

	if (swiper.isEnd && totalColumns > visibleColumns) {
		currentColumn = totalColumns - visibleColumns;
	}

	const maxColumn = Math.max(0, totalColumns - visibleColumns);

	return {
		rows,
		totalColumns,
		visibleColumns,
		currentColumn,
		maxColumn,
	};
}

/**
 * Calculates the optimal dynamic width for cards based on container width and card count
 */
export function calculateDynamicWidth(
	containerWidth: number,
	totalCards: number,
): number | null {
	if (containerWidth < 768) {
		// Mobile: calculate for 2-row grid
		const cardsPerRow = Math.ceil(totalCards / MOBILE_GRID_ROWS);
		const cardsThatFitPerRow = Math.floor(
			(containerWidth + SPACE_BETWEEN) /
				(BASE_CARD_WIDTH_MOBILE + SPACE_BETWEEN),
		);

		// If all cards fit in one row, calculate optimal width
		if (cardsThatFitPerRow >= cardsPerRow && cardsPerRow > 0) {
			const availableWidth = containerWidth - (cardsPerRow - 1) * SPACE_BETWEEN;
			const optimalWidth = Math.floor(availableWidth / cardsPerRow);
			// Only use if it's at least as wide as base (don't shrink)
			return optimalWidth >= BASE_CARD_WIDTH_MOBILE ? optimalWidth : null;
		}
		return null;
	} else {
		// Desktop: single row
		const cardsThatFit = Math.floor(
			(containerWidth + SPACE_BETWEEN) /
				(BASE_CARD_WIDTH_DESKTOP + SPACE_BETWEEN),
		);

		// If all cards fit, calculate optimal width to fill space
		if (cardsThatFit >= totalCards && totalCards > 0) {
			const availableWidth = containerWidth - (totalCards - 1) * SPACE_BETWEEN;
			const optimalWidth = Math.floor(availableWidth / totalCards);
			// Only use if it's at least as wide as base (don't shrink)
			return optimalWidth >= BASE_CARD_WIDTH_DESKTOP ? optimalWidth : null;
		}
		return null;
	}
}
